'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { saveUnitListing, type Listing } from '@repo/api';
import { SaveButton } from '@/components/ui';
import { useWithAuth } from '@/hooks/useWithAuth';
import { useStore } from '@/providers/StoreProvider';

export function SaveListing({
  listingId,
  listingSlug,
  listing,
  initialSaved,
  className,
  iconClassName,
  onSaveChange,
}: {
  listingId: string;
  listingSlug?: string;
  listing?: Listing;
  initialSaved?: boolean;
  className?: string;
  iconClassName?: string;
  onSaveChange?: (saved: boolean) => void;
}) {
  const withAuth = useWithAuth();
  // Subscribe to savedUnitIds array so component re-renders when it changes
  const savedUnitIds = useStore(s => s.savedUnits.savedUnitIds);
  const addSavedUnit = useStore(s => s.savedUnits.addSavedUnit);
  const removeSavedUnit = useStore(s => s.savedUnits.removeSavedUnit);

  const [isLoading, setIsLoading] = useState(false);

  // Ensure the store (source of truth) is seeded when we know this listing is already saved.
  useEffect(() => {
    if (!initialSaved || !listing || !listingId) return;
    if (savedUnitIds.includes(listingId)) return;
    addSavedUnit(listing);
  }, [addSavedUnit, initialSaved, listing, listingId, savedUnitIds]);

  const isSaved = useMemo(() => {
    if (listingId) return savedUnitIds.includes(listingId);
    return Boolean(initialSaved);
  }, [initialSaved, savedUnitIds, listingId]);

  const slug = listingSlug || listing?.slug || listingId;

  return (
    <SaveButton
      className={className}
      isSaved={isSaved}
      aria-label={isSaved ? 'Unsave listing' : 'Save listing'}
      disabled={isLoading}
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();

        if (!listingId || !slug) return;

        const nextSaved = !isSaved;

        void (async () => {
          let didOptimisticUpdate = false;
          try {
            setIsLoading(true);

            await withAuth(
              async () => {
                // Optimistic update - store is source of truth
                if (nextSaved) {
                  if (listing) addSavedUnit(listing);
                } else {
                  removeSavedUnit(listingId);
                }
                didOptimisticUpdate = true;
                onSaveChange?.(nextSaved);

                await saveUnitListing(slug, nextSaved);
              },
              {
                title: 'Log in required',
                description: 'Please log in to save listings.',
              },
            );
          } catch (err) {
            if (!didOptimisticUpdate) return;
            // Revert on error
            if (nextSaved) {
              removeSavedUnit(listingId);
            } else if (listing) {
              addSavedUnit(listing);
            }
            onSaveChange?.(!nextSaved);
            console.error('Error toggling save:', err);
          } finally {
            setIsLoading(false);
          }
        })();
      }}
      iconClassName={iconClassName}
    />
  );
}
